import { useEffect, useState } from "react";
import { getStoreTimezone } from "./store-timezone";
import {
  assignQuantityPromoDiscounts,
  computeStartingFromPrice,
  opcoesComPromocao,
  precoComPromocaoNasOpcoes,
  round2,
  type OptionGroup,
  type PromocaoNasOpcoes,
  type QuantityPromoConfig,
} from "@workspace/pricing";

export interface ProductPromotion {
  enabled: boolean;
  discountType: "percent" | "fixed";
  discountValue: number;
  scheduleType: "always" | "weekly" | "range";
  weekly?: {
    days: number[];
    hasTimeWindow: boolean;
    startTime?: string;
    endTime?: string;
  };
  range?: {
    startsAt: string;
    endsAt: string;
  };
  kind?: "simple" | "quantity";
  channels?: "both" | "pdv" | "link";
  quantity?: QuantityPromoConfig;
}

export type PromoChannel = "pdv" | "link";

/** Ausente = "both" (comportamento histórico: PDV e link sempre aplicaram). */
export function promotionAppliesToChannel(
  promo: ProductPromotion | null | undefined,
  channel: PromoChannel,
): boolean {
  const c = promo?.channels;
  return !c || c === "both" || c === channel;
}

export interface PromotionState {
  active: boolean;
  configured: boolean;
  originalPrice: number;
  finalPrice: number;
  discountAmount: number;
  discountPercent: number;
  endsLabel?: string;
}

const WEEKDAY_MAP: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export function getBrazilParts(now: Date) {
  // "Brazil" por herança de nome — na real é o relógio da LOJA (store-timezone).
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: getStoreTimezone(),
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value || "";
  const hour = Number(get("hour"));
  return {
    weekday: WEEKDAY_MAP[get("weekday")] ?? 0,
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hours: hour === 24 ? 0 : hour,
    minutes: Number(get("minute")),
  };
}

function formatBrDate(iso: string): string {
  try {
    const d = new Date(iso);
    const fmt = new Intl.DateTimeFormat("pt-BR", {
      timeZone: getStoreTimezone(),
      day: "2-digit",
      month: "2-digit",
    });
    return fmt.format(d);
  } catch {
    return iso;
  }
}

export function getActivePromotion(
  product: any,
  now: Date = new Date(),
  channel?: PromoChannel,
): PromotionState {
  const original = Number(product?.price) || 0;
  const promo = product?.promotion as ProductPromotion | null | undefined;
  const base: PromotionState = {
    active: false,
    configured: !!(promo && promo.enabled && promo.kind !== "quantity"),
    originalPrice: original,
    finalPrice: original,
    discountAmount: 0,
    discountPercent: 0,
  };

  if (!promo || !promo.enabled) return base;
  // Espelho do server: promo por quantidade NUNCA passa pelo caminho simple —
  // sem discountValue top-level ela viraria "fixed R$ 0" e zeraria o preço.
  if (promo.kind === "quantity") return base;
  if (channel && !promotionAppliesToChannel(promo, channel)) return base;

  let active = false;
  let endsLabel: string | undefined;
  const parts = getBrazilParts(now);

  if (promo.scheduleType === "always") {
    active = true;
  } else if (promo.scheduleType === "weekly" && promo.weekly) {
    const days = Array.isArray(promo.weekly.days) ? promo.weekly.days : [];
    if (days.includes(parts.weekday)) {
      if (!promo.weekly.hasTimeWindow) {
        active = true;
      } else {
        const start = promo.weekly.startTime || "00:00";
        const end = promo.weekly.endTime || "23:59";
        const [sh, sm] = start.split(":").map(Number);
        const [eh, em] = end.split(":").map(Number);
        const cur = parts.hours * 60 + parts.minutes;
        const startMin = sh * 60 + (sm || 0);
        const endMin = eh * 60 + (em || 0);
        const inWindow = endMin > startMin
          ? (cur >= startMin && cur < endMin)
          : (cur >= startMin || cur < endMin);
        if (inWindow) {
          active = true;
          endsLabel = `Promoção até às ${end}`;
        }
      }
    }
  } else if (promo.scheduleType === "range" && promo.range) {
    const start = new Date(promo.range.startsAt).getTime();
    const end = new Date(promo.range.endsAt).getTime();
    const t = now.getTime();
    if (!isNaN(start) && !isNaN(end) && t >= start && t <= end) {
      active = true;
      endsLabel = `Promoção até ${formatBrDate(promo.range.endsAt)}`;
    }
  }

  if (!active) return base;

  let finalPrice = original;
  if (promo.discountType === "percent") {
    const pct = Math.max(0, Math.min(100, Number(promo.discountValue) || 0));
    finalPrice = original * (1 - pct / 100);
  } else {
    const fixed = Math.max(0, Number(promo.discountValue) || 0);
    finalPrice = Math.min(original, fixed);
  }
  finalPrice = Math.max(0, Math.round(finalPrice * 100) / 100);
  const discountAmount = Math.max(0, Math.round((original - finalPrice) * 100) / 100);
  const discountPercent = original > 0 ? Math.round((discountAmount / original) * 100) : 0;

  return {
    active: true,
    configured: true,
    originalPrice: original,
    finalPrice,
    discountAmount,
    discountPercent,
    endsLabel,
  };
}

/** A promoção SIMPLES que vale sobre as opções de um produto de preço base 0 (o preço nas opções). Pedro,
 * 23/10/2026: «Se tem uma configuração, o sistema inteiro tem que seguir isso» — em % (o % no preço de cada opção) e,
 * desde 24/10/2026, em valor fixo (cada opção sai por no máximo o valor). A conta é a do servidor: opcoesComPromocao,
 * nos grupos que formam o preço (@workspace/pricing). null = sem efeito: produto com preço base, por peso, por
 * quantidade, fora da agenda ou do canal (sem canal, como a lista de Produtos do painel: qualquer canal). */
export function promocaoNasOpcoes(product: any, channel?: PromoChannel, now: Date = new Date()): PromocaoNasOpcoes | null {
  const base = Number(product?.price);
  if (Number.isFinite(base) && base > 0) return null;
  if (product?.soldByWeight) return null;
  const promo = product?.promotion as ProductPromotion | null | undefined;
  if (!promo || (promo.discountType !== "percent" && promo.discountType !== "fixed")) return null;
  if (!getActivePromotion(product, now, channel).active) return null;
  // Espelho do promocaoNasOpcoes do servidor: o % ≤ 0 não vale; o valor fixo vale até em 0 (min(preço, 0)), como no
  // produto com preço base.
  const valor = Number(promo.discountValue);
  if (!Number.isFinite(valor)) return null;
  if (promo.discountType === "percent") {
    const pct = Math.max(0, Math.min(100, valor));
    return pct > 0 ? { tipo: "percent", valor: pct } : null;
  }
  return { tipo: "fixed", valor: Math.max(0, valor) };
}

/** Os grupos de opções do produto como se vendem agora: com a promoção nos grupos que formam o preço quando
 * promocaoNasOpcoes vale, senão os de sempre. Usar onde o preço das opções vira tela ou conta (modal, carrinho,
 * «A partir de», PDV) — o servidor reprecifica com a mesma função. */
export function gruposComPromocao(product: any, channel?: PromoChannel, now: Date = new Date()): OptionGroup[] {
  const groups = (Array.isArray(product?.optionGroups) ? product.optionGroups : []) as OptionGroup[];
  const regra = promocaoNasOpcoes(product, channel, now);
  return regra ? opcoesComPromocao(groups, regra) : groups;
}

/** «A partir de» de produto com o preço nas opções: o valor (já com a promoção, se houver) e o original para riscar.
 * null = o produto tem preço base (ou nada a mostrar). */
export function aPartirDeComPromocao(
  product: any,
  channel?: PromoChannel,
  grupos: OptionGroup[] | null | undefined = product?.optionGroups,
  now: Date = new Date(),
): { valor: number; original: number | null } | null {
  const base = Number(product?.price);
  if (Number.isFinite(base) && base > 0) return null;
  const override = product?.startingPriceOverride;
  const original = computeStartingFromPrice(grupos, override);
  if (!original || original <= 0) return null;
  const regra = promocaoNasOpcoes(product, channel, now);
  if (!regra) return { valor: original, original: null };
  const ov = Number(override);
  const valor = computeStartingFromPrice(
    opcoesComPromocao(grupos || [], regra),
    Number.isFinite(ov) && ov > 0 ? precoComPromocaoNasOpcoes(ov, regra) : override,
  );
  return valor != null && valor < original ? { valor, original } : { valor: original, original: null };
}

/** Promo POR QUANTIDADE ativa (kind, agenda e canal) → config, senão null.
 * A conta em si mora em @workspace/pricing (quantityPromoTiers) — a MESMA que
 * o servidor usa pra dividir as linhas do pedido. */
export function getActiveQuantityPromo(
  product: any,
  now: Date = new Date(),
  channel: PromoChannel = "link",
): QuantityPromoConfig | null {
  const promo = product?.promotion as ProductPromotion | null | undefined;
  if (!promo || !promo.enabled || promo.kind !== "quantity" || !promo.quantity) return null;
  if (product?.soldByWeight) return null;
  if (!promotionAppliesToChannel(promo, channel)) return null;
  if (!isScheduleActiveClient(promo, now)) return null;
  return promo.quantity;
}

function isScheduleActiveClient(promo: ProductPromotion, now: Date): boolean {
  const parts = getBrazilParts(now);
  if (promo.scheduleType === "always") return true;
  if (promo.scheduleType === "weekly" && promo.weekly) {
    const days = Array.isArray(promo.weekly.days) ? promo.weekly.days : [];
    if (!days.includes(parts.weekday)) return false;
    if (!promo.weekly.hasTimeWindow) return true;
    const start = promo.weekly.startTime || "00:00";
    const end = promo.weekly.endTime || "23:59";
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const cur = parts.hours * 60 + parts.minutes;
    const startMin = sh * 60 + (sm || 0);
    const endMin = eh * 60 + (em || 0);
    return endMin > startMin ? cur >= startMin && cur < endMin : cur >= startMin || cur < endMin;
  }
  if (promo.scheduleType === "range" && promo.range) {
    const start = new Date(promo.range.startsAt).getTime();
    const end = new Date(promo.range.endsAt).getTime();
    const t = now.getTime();
    return !isNaN(start) && !isNaN(end) && t >= start && t <= end;
  }
  return false;
}

export interface QuantityPromoDiscountEntry {
  productId: string;
  productName: string;
  discount: number;
}

/** Desconto da promo POR QUANTIDADE por produto (unidades somadas entre as
 * linhas do carrinho; desconto só no preço-base). MESMA fórmula que o servidor
 * usa pra dividir as linhas do pedido (quantityPromoTiers) — os totais têm que
 * bater, senão o pagamento misto é rejeitado ("a soma não bate"). */
export function quantityPromoDiscounts(
  items: Array<{ product: any; quantity: number; optionsTotal?: number }>,
  channel: PromoChannel,
  now: Date = new Date(),
): QuantityPromoDiscountEntry[] {
  // O VALOR da unidade é base + adicionais da linha (tamanho como adicional,
  // base R$ 0 — caso Blusa). Uma entrada por unidade, na ordem das linhas:
  // MESMA conta que o servidor usa pra dividir as linhas do pedido.
  const byProduct = new Map<string, { name: string; values: number[]; cfg: QuantityPromoConfig }>();
  for (const it of items) {
    const cfg = getActiveQuantityPromo(it.product, now, channel);
    if (!cfg) continue;
    const id = String(it.product?.id ?? "");
    const value = round2((Number(it.product?.price) || 0) + (Number(it.optionsTotal) || 0));
    let cur = byProduct.get(id);
    if (!cur) {
      cur = { name: String(it.product?.name ?? ""), values: [], cfg };
      byProduct.set(id, cur);
    }
    for (let i = 0; i < Math.max(0, Math.trunc(it.quantity || 0)); i++) cur.values.push(value);
  }
  const out: QuantityPromoDiscountEntry[] = [];
  for (const [productId, g] of byProduct) {
    const discounts = assignQuantityPromoDiscounts(g.values, g.cfg);
    const discount = round2(discounts.reduce((s, d) => s + d, 0));
    if (discount > 0) out.push({ productId, productName: g.name, discount });
  }
  return out;
}

/** Desconto POR LINHA do carrinho (alinhado ao índice do array de entrada):
 * mesma atribuição da conta única — flatten das unidades na ordem das linhas
 * por produto, rank por valor DESC, desconto somado de volta na linha dona da
 * unidade. É o que permite riscar o preço linha a linha (pedido do Pedro,
 * 09/08): o desconto de uma linha DEPENDE das outras do mesmo produto. */
/** Desconto POR UNIDADE, por linha (out[linha][unidade]) — é o que permite
 * riscar unidade a unidade na mensagem do WhatsApp (bloco "1 de 3"). Mesma
 * atribuição da conta única. */
export function quantityPromoUnitDiscountsByLine(
  items: Array<{ product: any; quantity: number; optionsTotal?: number }>,
  channel: PromoChannel,
  now: Date = new Date(),
): number[][] {
  const out: number[][] = items.map((it) =>
    Array(Math.max(0, Math.trunc(it.quantity || 0))).fill(0),
  );
  const groups = new Map<string, number[]>(); // productId → índices das linhas
  for (let i = 0; i < items.length; i++) {
    const cfg = getActiveQuantityPromo(items[i].product, now, channel);
    if (!cfg) continue;
    const id = String(items[i].product?.id ?? "");
    const g = groups.get(id);
    if (g) g.push(i);
    else groups.set(id, [i]);
  }
  for (const idxs of groups.values()) {
    const cfg = getActiveQuantityPromo(items[idxs[0]].product, now, channel);
    if (!cfg) continue;
    const unitValues: number[] = [];
    const owner: Array<{ line: number; unit: number }> = [];
    for (const i of idxs) {
      const it = items[i];
      const value = round2((Number(it.product?.price) || 0) + (Number(it.optionsTotal) || 0));
      for (let k = 0; k < Math.max(0, Math.trunc(it.quantity || 0)); k++) {
        unitValues.push(value);
        owner.push({ line: i, unit: k });
      }
    }
    const discounts = assignQuantityPromoDiscounts(unitValues, cfg);
    discounts.forEach((d, u) => {
      out[owner[u].line][owner[u].unit] = d;
    });
  }
  return out;
}

export function quantityPromoLineDiscounts(
  items: Array<{ product: any; quantity: number; optionsTotal?: number }>,
  channel: PromoChannel,
  now: Date = new Date(),
): number[] {
  return quantityPromoUnitDiscountsByLine(items, channel, now).map((units) =>
    round2(units.reduce((s, d) => s + d, 0)),
  );
}

/** Rótulo curto da promo por quantidade (selo no card e no carrinho). */
/** Dica dinâmica do carrinho: conta o que JÁ tem desconto e o que falta pra
 * próxima (padrão Mercado Livre — pedido do Pedro, 09/08). O caso que motivou:
 * no "a cada 3", a 4ª unidade volta ao preço cheio e sem a dica parecia bug. */
export function quantityPromoHint(
  cfg: QuantityPromoConfig | null | undefined,
  totalQty: number,
): string | null {
  if (!cfg) return null;
  const qty = Math.max(0, Math.trunc(Number(totalQty) || 0));
  if (qty === 0) return null;
  const money = (v?: number) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;
  const un = (n: number) => `${n} unidade${n === 1 ? "" : "s"}`;

  if (cfg.mode === "threshold" && cfg.fromUnit) {
    const from = Math.max(2, Math.trunc(Number(cfg.fromUnit)));
    const offer = cfg.discountType === "fixed"
      ? `${money(cfg.discountValue)} cada`
      : `${cfg.discountValue}% de desconto`;
    if (qty < from) {
      const falta = from - qty;
      return `${falta === 1 ? "Falta" : "Faltam"} ${un(falta)} para começar: ${offer} da ${from}ª em diante`;
    }
    return `${un(qty - from + 1)} com desconto aplicado ✓`;
  }

  // Levando N ou mais, TODAS com desconto (Pedro, 10/10/2026): abaixo de N mostra quanto falta.
  if (cfg.mode === "all_from" && cfg.fromUnit) {
    const from = Math.max(2, Math.trunc(Number(cfg.fromUnit)));
    if (qty < from) {
      const offer = cfg.discountType === "fixed"
        ? `todas saem por ${money(cfg.discountValue)}`
        : `todas saem com ${cfg.discountValue}% de desconto`;
      return `Leve mais ${un(from - qty)} e ${offer}`;
    }
    return `${un(qty)} com desconto aplicado ✓`;
  }

  if (cfg.mode === "every_nth" && cfg.n) {
    const n = Math.max(2, Math.trunc(Number(cfg.n)));
    const ganhas = Math.floor(qty / n);
    const resto = qty % n;
    const falta = resto === 0 ? n : n - resto;
    const gratis = cfg.discountType === "percent" && Number(cfg.discountValue) >= 100;
    const offer = gratis
      ? "grátis"
      : cfg.discountType === "fixed"
        ? `por ${money(cfg.discountValue)}`
        : `com ${cfg.discountValue}% de desconto`;
    if (ganhas === 0) {
      return `Adicione mais ${un(falta)} e 1 sai ${offer}`;
    }
    return `${ganhas} ${gratis ? (ganhas === 1 ? "grátis" : "grátis") : "com desconto"} ✓ · mais ${un(falta)} e outra sai ${offer}`;
  }

  if (cfg.mode === "progressive" && cfg.step && cfg.maxDiscount != null) {
    const stepVal = Math.max(0, Number(cfg.step.discountValue) || 0);
    const cap = Number(cfg.maxDiscount);
    const isPct = cfg.step.discountType === "percent";
    // No teto o valor para de crescer, mas as próximas CONTINUAM com o
    // desconto — "máximo atingido" lia-se como fim da promoção (Pedro, 09/08).
    const proxima = Math.min(cap, stepVal * qty);
    const fmt = (v: number) => (isPct ? `${v}%` : money(v));
    return `A próxima unidade sai com ${fmt(proxima)} de desconto`;
  }

  return null;
}

/** Dica POR LINHA do carrinho: a contagem é do que ESTA linha carrega
 * (lineDiscCount = unidades descontadas na linha — "2 com desconto" num card
 * de 1 unidade lia-se como erro; Pedro, 09/08). A chamada pra próxima
 * (every_nth/progressivo) é do produto e repete junto. Retorna null quando a
 * linha não tem desconto — o fallback abaixo do gatilho é o quantityPromoHint
 * na última linha do produto. */
export function quantityPromoLineHint(
  cfg: QuantityPromoConfig | null | undefined,
  totalQty: number,
  lineDiscCount: number,
): string | null {
  if (!cfg || lineDiscCount <= 0) return null;
  const qty = Math.max(0, Math.trunc(Number(totalQty) || 0));
  const money = (v?: number) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;
  const un = (n: number) => `${n} unidade${n === 1 ? "" : "s"}`;

  if (cfg.mode === "threshold" || cfg.mode === "all_from") {
    return `${un(lineDiscCount)} com desconto aplicado ✓`;
  }
  if (cfg.mode === "every_nth" && cfg.n) {
    const n = Math.max(2, Math.trunc(Number(cfg.n)));
    const resto = qty % n;
    const falta = resto === 0 ? n : n - resto;
    const gratis = cfg.discountType === "percent" && Number(cfg.discountValue) >= 100;
    const offer = gratis
      ? "grátis"
      : cfg.discountType === "fixed"
        ? `por ${money(cfg.discountValue)}`
        : `com ${cfg.discountValue}% de desconto`;
    return `${un(lineDiscCount)} ${gratis ? "grátis" : "com desconto"} ✓ · mais ${un(falta)} e outra sai ${offer}`;
  }
  if (cfg.mode === "progressive" && cfg.step && cfg.maxDiscount != null) {
    const stepVal = Math.max(0, Number(cfg.step.discountValue) || 0);
    const cap = Number(cfg.maxDiscount);
    const proxima = Math.min(cap, stepVal * qty);
    const fmt = (v: number) => (cfg.step!.discountType === "percent" ? `${v}%` : money(v));
    // "sai com R$ 5,00" lia-se como PREÇO — sempre com "de desconto" no fim.
    return `${un(lineDiscCount)} com desconto ✓ · a próxima sai com ${fmt(proxima)} de desconto`;
  }
  return null;
}

/** Frases escolhidas pelo Pedro (09/08): sem "off" — "de desconto" é a língua
 * do balcão. Leve N pague N−1 continua sendo o caso especial de 100%. */
export function quantityPromoLabel(cfg: QuantityPromoConfig | null | undefined): string | null {
  if (!cfg) return null;
  const money = (v?: number) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;
  if (cfg.mode === "threshold" && cfg.fromUnit) {
    return cfg.discountType === "fixed"
      ? `${money(cfg.discountValue)} cada, da ${cfg.fromUnit}ª em diante`
      : `${cfg.discountValue}% de desconto da ${cfg.fromUnit}ª em diante`;
  }
  if (cfg.mode === "all_from" && cfg.fromUnit) {
    return cfg.discountType === "fixed"
      ? `${money(cfg.discountValue)} cada, levando ${cfg.fromUnit} ou mais`
      : `${cfg.discountValue}% de desconto levando ${cfg.fromUnit} ou mais`;
  }
  if (cfg.mode === "every_nth" && cfg.n) {
    if (cfg.discountType === "percent" && Number(cfg.discountValue) >= 100) {
      return `Leve ${cfg.n}, pague ${cfg.n - 1}`;
    }
    return cfg.discountType === "fixed"
      ? `A cada ${cfg.n}, 1 sai por ${money(cfg.discountValue)}`
      : `A cada ${cfg.n}, 1 sai com ${cfg.discountValue}% de desconto`;
  }
  if (cfg.mode === "progressive" && cfg.step) {
    const cap = cfg.step.discountType === "percent"
      ? `${cfg.maxDiscount}%`
      : money(cfg.maxDiscount);
    return `Leve mais, pague menos: até ${cap} de desconto`;
  }
  return null;
}

export function usePromotionTick(intervalMs = 30000): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return tick;
}
